import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getPriorityCountryCodes, getOtherCountryCodes } from "@/utils/countries";

const PhoneInput = ({ value, onChange, className = "" }) => {
  const priorityCodes = getPriorityCountryCodes();
  const otherCodes = getOtherCountryCodes();
  
  // Parse initial value
  const parsePhone = (phone) => {
    if (!phone) return { code: "+52", number: "" };
    
    // Try to extract country code
    for (const c of [...priorityCodes, ...otherCodes]) {
      if (phone.startsWith(c.code)) {
        return { code: c.code, number: phone.slice(c.code.length).trim() };
      }
    }
    
    // Default to Mexico if no code found
    return { code: "+52", number: phone.replace(/^\+?\d{1,4}\s*/, "") };
  };

  const [phoneData, setPhoneData] = useState(() => parsePhone(value));

  useEffect(() => {
    const parsed = parsePhone(value);
    setPhoneData(parsed);
  }, [value]);

  const handleCodeChange = (newCode) => {
    setPhoneData(prev => ({ ...prev, code: newCode }));
    onChange(`${newCode} ${phoneData.number}`);
  };

  const handleNumberChange = (e) => {
    // Only allow numbers
    const newNumber = e.target.value.replace(/[^\d]/g, "");
    setPhoneData(prev => ({ ...prev, number: newNumber }));
    onChange(`${phoneData.code} ${newNumber}`);
  };

  const getSelectedFlag = () => {
    const found = [...priorityCodes, ...otherCodes].find(c => c.code === phoneData.code);
    return found ? found.flag : "🌍";
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <Select value={phoneData.code} onValueChange={handleCodeChange}>
        <SelectTrigger className="w-[140px]">
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{getSelectedFlag()}</span>
              <span>{phoneData.code}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {/* Priority countries */}
          {priorityCodes.map((c, idx) => (
            <SelectItem key={`priority-${idx}`} value={c.code}>
              <span className="flex items-center gap-2">
                <span>{c.flag}</span>
                <span>{c.code}</span>
                <span className="text-slate-500 text-xs">{c.country}</span>
              </span>
            </SelectItem>
          ))}
          {/* Separator */}
          <SelectItem value="---separator---" disabled>
            <span className="text-slate-300">──────────────</span>
          </SelectItem>
          {/* Other countries */}
          {otherCodes.map((c, idx) => (
            <SelectItem key={`other-${idx}`} value={c.code}>
              <span className="flex items-center gap-2">
                <span>{c.flag}</span>
                <span>{c.code}</span>
                <span className="text-slate-500 text-xs">{c.country}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="text"
        value={phoneData.number}
        onChange={handleNumberChange}
        placeholder="Número de teléfono"
        className="flex-1"
        inputMode="numeric"
      />
    </div>
  );
};

export default PhoneInput;
